/**
 * Export capture session — the single place that owns the hidden capture
 * iframe for the HTML export pipeline.
 *
 * Previously the dialog preview and htmlToPdf each built their own iframe,
 * doc.write'd the content, waited for resources and duplicated ~150 lines of
 * helpers — every Download/Save click paid the whole pipeline again. A
 * CaptureSession is created once when the export dialog opens, both the
 * preview and the final export render against it, and it's disposed when the
 * dialog closes.
 */

// ─── Shared constants/helpers (formerly duplicated in htmlToPdf + dialog) ───

export const VIEWPORT_WIDTH = 1280

/** Page dimensions in mm. */
export const PAGE_DIMS: Record<string, [number, number]> = {
  a4: [210, 297],
  letter: [215.9, 279.4],
  legal: [215.9, 355.6],
}

export function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.replace('#', ''), 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

export function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')
}

/** Inject <base href> for cross-file resource resolution. */
export function injectBaseHref(html: string, parentId: string): string {
  const baseTag = `<base href="/preview/${parentId}/">`
  const match = html.match(/<head[^>]*>/i)
  if (match) {
    const idx = html.indexOf(match[0]) + match[0].length
    return html.slice(0, idx) + baseTag + html.slice(idx)
  }
  return baseTag + html
}

/**
 * Strip active content from untrusted HTML: <script> elements, inline on*
 * handlers, and javascript: URLs. Used on the FALLBACK export path (raw
 * source) — the snapshot path arrives pre-sanitized by preview-snapshot.
 * The capture iframe is sandboxed (no scripts) regardless; this is defence in
 * depth for anything that clones capture-doc nodes toward the parent document
 * (the snapdom engine does, briefly — spike-verified inert, but never rely on
 * a library's internals for an origin boundary).
 */
export function sanitizeUntrustedHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html') // inert: never executes scripts
  for (const s of doc.querySelectorAll('script')) s.remove()
  const walker = doc.createTreeWalker(doc.documentElement, NodeFilter.SHOW_ELEMENT)
  let el: Element | null = doc.documentElement
  while (el) {
    for (const attr of [...el.attributes]) {
      const name = attr.name.toLowerCase()
      if (name.startsWith('on')) el.removeAttribute(attr.name)
      else if ((name === 'href' || name === 'src' || name === 'xlink:href') && /^\s*javascript:/i.test(attr.value)) {
        el.removeAttribute(attr.name)
      }
    }
    el = walker.nextNode() as Element | null
  }
  return '<!DOCTYPE html>' + doc.documentElement.outerHTML
}

// ─── Background info ─────────────────────────────────────────────────────

/**
 * One source of truth for the document's background — replaces the old
 * findBgCss (which treated flat colors as "has a background", paying a
 * full-height raster pass for nothing) and sampleBgColor (a single content
 * pixel — wrong on gradients, and the preview sampled a different canvas than
 * the export, so the swatch could disagree with the output).
 */
export interface BgInfo {
  /** True when a background-image (gradient/url) exists → a bg layer raster is needed. */
  hasImage: boolean
  /** Computed background as LONGHAND declarations (`background-color: …; background-image: …; …`)
   *  for re-rendering the layer. Never the `background` shorthand: Firefox
   *  serializes the computed shorthand as "" — reading it blanked the
   *  extend-mode bg layer on Firefox. */
  css: string
  /** Effective flat background color (composited over white when translucent). */
  color: [number, number, number]
}

/** All background longhands, as computed declarations. */
function bgDeclarations(style: CSSStyleDeclaration): string {
  return [
    `background-color: ${style.backgroundColor}`,
    `background-image: ${style.backgroundImage}`,
    `background-position: ${style.backgroundPosition}`,
    `background-size: ${style.backgroundSize}`,
    `background-repeat: ${style.backgroundRepeat}`,
    `background-origin: ${style.backgroundOrigin}`,
    `background-clip: ${style.backgroundClip}`,
    `background-attachment: ${style.backgroundAttachment}`,
  ].join(';')
}

/** True when the computed style paints any background (image layer or non-transparent color). */
function hasVisibleBg(style: CSSStyleDeclaration): boolean {
  return style.backgroundImage !== 'none'
    || !(style.backgroundColor === 'transparent' || style.backgroundColor === 'rgba(0, 0, 0, 0)')
}

// Normalize ANY computed CSS color to RGBA by painting it on a 1×1 canvas.
// A regex on `rgb()/rgba()` misses CSS Color 4: modern browsers serialize
// oklch()/lab()/color()/color-mix() backgrounds in their own functional form
// (only legacy sRGB colors come back as rgb()), which used to silently fall
// through to the white default on dark oklch-themed documents.
let colorProbe: CanvasRenderingContext2D | null | undefined
function parseCssColor(cssColor: string): [number, number, number, number] | null {
  if (colorProbe === undefined) {
    const c = document.createElement('canvas')
    c.width = c.height = 1
    colorProbe = c.getContext('2d', { willReadFrequently: true })
  }
  if (!colorProbe) return null
  colorProbe.clearRect(0, 0, 1, 1)
  colorProbe.fillStyle = cssColor
  colorProbe.fillRect(0, 0, 1, 1)
  const d = colorProbe.getImageData(0, 0, 1, 1).data
  return [d[0], d[1], d[2], d[3] / 255]
}

export function getBgInfo(doc: Document): BgInfo {
  const win = doc.defaultView!
  let hasImage = false
  let css = ''
  let color: [number, number, number] = [255, 255, 255]
  let colorFound = false
  for (const el of [doc.body, doc.documentElement, doc.body?.firstElementChild]) {
    if (!el) continue
    const style = win.getComputedStyle(el)
    if (!hasImage && style.backgroundImage && style.backgroundImage !== 'none') {
      hasImage = true
      css = bgDeclarations(style)
    }
    if (!colorFound) {
      const parsed = parseCssColor(style.backgroundColor)
      if (parsed && parsed[3] > 0) {
        const [r, g, b, a] = parsed
        // Composite over white so a translucent color still yields a usable flat fill.
        color = [
          Math.round(r * a + 255 * (1 - a)),
          Math.round(g * a + 255 * (1 - a)),
          Math.round(b * a + 255 * (1 - a)),
        ]
        colorFound = true
      }
    }
    if (hasImage && colorFound) break
  }
  return { hasImage, css, color }
}

// ─── Resource waiting ────────────────────────────────────────────────────

const PER_ASSET_TIMEOUT_MS = 5000
const FONTS_TIMEOUT_MS = 2000
/**
 * Preview settle budget. The capture session becomes usable (dialog preview
 * renders) after AT MOST this long, even while slow assets are still loading —
 * the full per-asset wait continues in the background and `session.settled`
 * resolves when it finishes (the dialog re-renders the preview then). Exports
 * always await the full settle; only the preview is gated. Without this, one
 * hanging asset held the preview for the whole PER_ASSET_TIMEOUT_MS (and a
 * hanging asset could stall document `load` — which fires only after
 * subresources — indefinitely).
 */
const PREVIEW_SETTLE_BUDGET_MS = 700

/**
 * Wait for external stylesheets, images, and fonts. Each asset gets its own
 * timeout (one hanging asset no longer burns the whole budget for the rest).
 * Returns whether anything timed out so the caller can warn the user that the
 * export may be missing assets.
 */
export async function waitForResources(doc: Document): Promise<{ timedOut: boolean }> {
  let timedOut = false
  // The timer must be CLEARED when the asset settles first — an orphaned timer
  // firing later would flip `timedOut` for an asset that actually loaded, and
  // the flag is read only at return time (which the fonts wait below can push
  // past the 5s mark): a false "assets may be missing" warning.
  const withCap = (p: Promise<void>) =>
    new Promise<void>((resolve) => {
      const timer = setTimeout(() => { timedOut = true; resolve() }, PER_ASSET_TIMEOUT_MS)
      p.then(() => { clearTimeout(timer); resolve() })
    })

  const pending: Promise<void>[] = []
  for (const link of doc.querySelectorAll('link[rel="stylesheet"]')) {
    if (!(link as HTMLLinkElement).sheet) {
      pending.push(withCap(new Promise<void>((r) => {
        link.addEventListener('load', () => r(), { once: true })
        link.addEventListener('error', () => r(), { once: true })
      })))
    }
  }
  for (const img of doc.querySelectorAll('img')) {
    if (!(img as HTMLImageElement).complete) {
      pending.push(withCap(new Promise<void>((r) => {
        img.addEventListener('load', () => r(), { once: true })
        img.addEventListener('error', () => r(), { once: true })
      })))
    }
  }
  if (pending.length > 0) await Promise.all(pending)
  if (doc.fonts?.ready) {
    await Promise.race([doc.fonts.ready.then(() => {}), new Promise<void>((r) => setTimeout(r, FONTS_TIMEOUT_MS))])
  }
  return { timedOut }
}

// ─── Text runs + break rects ─────────────────────────────────────────────

export interface TextRun {
  text: string
  x: number // iframe px from left
  y: number // iframe px from top
  w: number
  h: number
  fontSize: number // CSS px
}

/** Walk the DOM and collect text positions for the PDF text overlay. */
export function collectTextRuns(doc: Document): TextRun[] {
  const runs: TextRun[] = []
  const win = doc.defaultView!
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT)
  const range = doc.createRange()
  let node: Text | null

  while ((node = walker.nextNode() as Text | null)) {
    const text = node.textContent
    if (!text || !text.trim()) continue

    const parent = node.parentElement
    if (!parent) continue
    const style = win.getComputedStyle(parent)
    if (style.display === 'none' || style.visibility === 'hidden') continue

    const fontSize = parseFloat(style.fontSize) || 16

    // Apply CSS text-transform so overlay text matches the visual rendering
    const tt = style.textTransform
    const transform = (s: string) =>
      tt === 'uppercase' ? s.toUpperCase() :
      tt === 'lowercase' ? s.toLowerCase() :
      tt === 'capitalize' ? s.replace(/\b\w/g, (c) => c.toUpperCase()) : s

    // Check if text contains non-Latin characters (CJK, Arabic, Cyrillic, etc.)
    const hasNonLatin = /[^\u0000-\u024F]/.test(text)

    if (hasNonLatin) {
      // Per-character collection: each char gets its exact visual position from the DOM.
      // Handles mixed scripts (different fonts per char) and RTL correctly.
      // Iterate CODE POINTS, not UTF-16 units — astral chars (emoji, CJK Ext-B)
      // are surrogate pairs, and a per-unit loop would split them into two lone
      // surrogates that extract as U+FFFD garbage from the PDF overlay.
      for (let i = 0; i < text.length; ) {
        const ch = String.fromCodePoint(text.codePointAt(i)!)
        const len = ch.length
        if (!ch.trim()) { i += len; continue }
        range.setStart(node, i)
        range.setEnd(node, i + len)
        const r = range.getBoundingClientRect()
        if (r.width > 0 && r.height > 0) {
          runs.push({ text: transform(ch), x: r.x, y: r.y, w: r.width, h: r.height, fontSize })
        }
        i += len
      }
      continue
    }

    range.selectNodeContents(node)
    const rects = range.getClientRects()
    if (rects.length === 0) continue

    if (rects.length === 1) {
      const r = rects[0]
      if (r.width > 0 && r.height > 0) {
        runs.push({ text: transform(text.trim()), x: r.x, y: r.y, w: r.width, h: r.height, fontSize })
      }
      continue
    }

    // Multi-line: split by visual line using binary search for line boundaries
    const lineRects = Array.from(rects).filter((r) => r.width > 0 && r.height > 0)
    if (lineRects.length === 0) continue

    let searchStart = 0
    for (let li = 0; li < lineRects.length; li++) {
      const nextRect = lineRects[li + 1]
      let lineEnd: number

      if (!nextRect) {
        lineEnd = text.length
      } else {
        // Binary search: find first char on the next visual line
        let lo = searchStart, hi = text.length
        while (lo < hi) {
          const mid = (lo + hi) >> 1
          range.setStart(node, mid)
          range.setEnd(node, Math.min(mid + 1, text.length))
          const r = range.getBoundingClientRect()
          if (r.top < nextRect.top - 1) lo = mid + 1
          else hi = mid
        }
        lineEnd = lo
      }

      const lineText = text.substring(searchStart, lineEnd).trim()
      if (lineText) {
        const r = lineRects[li]
        runs.push({ text: transform(lineText), x: r.x, y: r.y, w: r.width, h: r.height, fontSize })
      }
      searchStart = lineEnd
    }
  }

  return runs
}

/**
 * Collect the vertical bands page breaks must not cut through: every text
 * run, atomic visuals (img/canvas/svg/video/table rows), and anything with
 * CSS break-inside: avoid.
 */
export function collectBreakRects(doc: Document, textRuns: TextRun[]): { top: number; bottom: number }[] {
  const rects: { top: number; bottom: number }[] = textRuns.map((r) => ({ top: r.y, bottom: r.y + r.h }))
  const win = doc.defaultView!
  for (const el of doc.body.querySelectorAll<HTMLElement>('img, canvas, svg, video, tr')) {
    const r = el.getBoundingClientRect()
    if (r.height > 0) rects.push({ top: r.y, bottom: r.y + r.height })
  }
  // break-inside: avoid — one computed-style read per element is acceptable
  // here; the walk already forced layout for the rects above.
  for (const el of doc.body.querySelectorAll<HTMLElement>('*')) {
    const bi = win.getComputedStyle(el).breakInside
    if (bi === 'avoid' || bi === 'avoid-page') {
      const r = el.getBoundingClientRect()
      if (r.height > 0) rects.push({ top: r.y, bottom: r.y + r.height })
    }
  }
  return rects
}

// ─── The session ─────────────────────────────────────────────────────────

export interface CaptureWarning {
  kind: 'resource-timeout' | 'snapshot-fallback'
  detail?: string
}

export interface CaptureSession {
  readonly doc: Document
  readonly warnings: CaptureWarning[]
  /**
   * Resolves when the FULL resource settle (per-asset waits + fonts) finished.
   * `true` = assets were still pending when the preview gate opened — the
   * session was remeasured and the caller should re-render its preview.
   * `false` = everything had already settled (or the session was disposed).
   * Exports must (and do — generateExportBlob awaits this) render post-settle.
   */
  readonly settled: Promise<boolean>
  width(): number
  contentHeight(): number
  /** Resize the capture viewport and re-settle. Returns the new content height. */
  setWidth(w: number): Promise<number>
  /** Re-measure content height (content may reflow as images/fonts arrive). */
  remeasure(): Promise<number>
  bgInfo(): BgInfo
  collectTextRuns(): TextRun[]
  collectBreakRects(runs: TextRun[]): { top: number; bottom: number }[]
  /** Run fn with html/body/first-child backgrounds stripped (extend mode), restoring after. */
  withBgStripped<T>(fn: () => Promise<T>): Promise<T>
  /** Mount a full-height div painting the doc background; remove() when captured. */
  mountBgLayer(totalHeightCss: number): Promise<{ el: HTMLElement; remove: () => void }>
  dispose(): void
}

// Frame waits must run on the PARENT window, never the capture iframe's own:
// the iframe sits off-viewport (left: -10000px), and browsers starve rAF
// there — Firefox throttles to ~1 tick/s, WebKit never fires it (preview
// took ~6s on Firefox and hung forever on Safari). Same-origin layout reads
// (scrollHeight/getClientRects) force a synchronous reflow of the iframe
// regardless of which window paces the wait, so the parent's clock is safe.
const raf2 = () => new Promise<void>((r) => window.requestAnimationFrame(() => window.requestAnimationFrame(() => r())))

export interface CreateCaptureSessionOptions {
  parentId?: string | null
  width: number
  /** True when `html` is raw (non-snapshot) source: sanitized + warned. */
  snapshotFallback?: boolean
}

export async function createCaptureSession(html: string, opts: CreateCaptureSessionOptions): Promise<CaptureSession> {
  const warnings: CaptureWarning[] = []
  let prepared = html
  if (opts.snapshotFallback) {
    prepared = sanitizeUntrustedHtml(prepared)
    warnings.push({ kind: 'snapshot-fallback' })
  }
  if (opts.parentId) prepared = injectBaseHref(prepared, opts.parentId)

  const iframe = document.createElement('iframe')
  // SECURITY: this iframe is appended to the MAIN app document (app origin),
  // and `htmlContent` is untrusted user/agent-authored HTML. `allow-same-origin`
  // (and ONLY that — no `allow-scripts`) lets the rasterizer read `contentDocument`
  // to rasterize the static DOM, while the document's own `<script>` tags never
  // execute. Without the sandbox attribute, opening the export dialog on a
  // malicious .html would run its JS first-party on the app origin with the
  // user's cookies. Dynamic/JS-rendered content is covered by the session-mode
  // snapshot path (requestSnapshot); this inline fallback renders static only.
  iframe.setAttribute('sandbox', 'allow-same-origin')
  // Tall initial height so flex/100vh-based layouts (CodeMirror, grids,
  // editors) have room to render to natural size. Without this the
  // default iframe height (~150px) collapses viewport-relative layouts
  // and the capture sees a blank page. Resized to actual content height
  // after measurement below.
  iframe.style.cssText = [
    'position: fixed', 'left: -10000px', 'top: 0',
    `width: ${opts.width}px`, 'height: 2000px',
    'border: none', 'visibility: hidden',
  ].join(';')
  document.body.appendChild(iframe)

  let disposed = false
  let width = opts.width
  let contentHeight = 0

  try {
    const doc = iframe.contentDocument!
    doc.open()
    doc.write(prepared)
    doc.close()
    const win = doc.defaultView!

    // Lazy-loaded images may never fire load/error in a hidden, off-screen
    // iframe (no viewport proximity as far as the browser is concerned) — each
    // one would burn its full per-asset timeout AND ship blank in the export.
    // Everything must render for capture anyway: force eager.
    for (const img of doc.querySelectorAll('img[loading="lazy"]')) {
      ;(img as HTMLImageElement).loading = 'eager'
    }

    // The DOM is fully parsed after doc.close() (doc.write parses
    // synchronously); only subresources are possibly pending. One double-rAF
    // for first layout, then the FULL settle (document load → per-asset waits
    // → fonts) runs as a raceable chain: document `load` itself waits on
    // images, so it must sit behind the preview gate too — a hanging asset
    // used to block the session (and the dialog) indefinitely right here.
    await raf2()
    const settleChain: Promise<{ timedOut: boolean }> = (async () => {
      if (doc.readyState !== 'complete') {
        await new Promise<void>((resolve) => {
          const check = () => { if (doc.readyState === 'complete') resolve() }
          doc.addEventListener('readystatechange', check)
          iframe.addEventListener('load', () => resolve(), { once: true })
          check() // re-check after listener attach (race-proof)
        })
      }
      return waitForResources(doc)
    })()

    // Preview gate: usable after full settle OR the budget, whichever first.
    const GATE = Symbol('gate')
    const pendingAtGate = GATE === await Promise.race([
      settleChain,
      new Promise<typeof GATE>((resolve) => setTimeout(() => resolve(GATE), PREVIEW_SETTLE_BUDGET_MS)),
    ])

    // Reset margin AND free body from viewport-relative sizing — apps often
    // use `body { height: 100vh }` for in-app scrolling, which pads the
    // capture with empty space. Forcing auto + 0 min-height lets body
    // collapse to natural content.
    const fix = doc.createElement('style')
    fix.textContent = 'html, body { margin: 0 !important; overflow: hidden !important; height: auto !important; min-height: 0 !important; }'
    doc.head.appendChild(fix)

    // Flatten @media rules to their current evaluation. The rasterizer copies
    // stylesheets into an SVG-foreignObject image, and browsers disagree about
    // the media environment inside an SVG image: Firefox does NOT re-evaluate
    // width media queries against the SVG viewport, so a doc that wrapped its
    // grid under `@media (max-width: …)` rendered the un-matched wide layout
    // clipped to the capture width (telemetry-monitor's stat cards, 2026-07).
    // The capture width is fixed and known here, so bake each @media block in
    // place: splice matching blocks' rules into the cascade position they
    // already occupy, drop non-matching ones. Re-runs from the ORIGINAL text
    // after every width change (setWidth) — flattened sheets can't re-evaluate
    // on their own. <link> sheets are not handled: the snapshot path inlines
    // them into <style> tags before we ever see the doc.
    const originalStyleTexts = new WeakMap<HTMLStyleElement, string>()
    // Sheets with no @media rules never need re-flattening — skip them on later
    // passes (each setWidth re-runs this; resetting textContent on a no-media
    // sheet forces a pointless full CSSOM reparse + style recalc per pass).
    const noMediaSheets = new WeakSet<HTMLStyleElement>()
    const flattenMediaRules = () => {
      for (const styleEl of doc.querySelectorAll('style')) {
        if (styleEl === fix) continue
        if (noMediaSheets.has(styleEl)) continue
        if (!originalStyleTexts.has(styleEl)) originalStyleTexts.set(styleEl, styleEl.textContent ?? '')
        else styleEl.textContent = originalStyleTexts.get(styleEl)!  // reset before re-flatten
        const sheet = styleEl.sheet
        if (!sheet) continue
        let out = ''
        let changed = false
        try {
          for (const rule of sheet.cssRules) {
            if (rule.type === CSSRule.MEDIA_RULE) {
              changed = true
              const media = rule as CSSMediaRule
              if (win.matchMedia(media.media.mediaText).matches) {
                for (const inner of media.cssRules) out += inner.cssText + '\n'
              }
            } else {
              out += rule.cssText + '\n'
            }
          }
        } catch { continue } // cssRules access failed — leave the sheet as-is
        if (changed) styleEl.textContent = out
        else noMediaSheets.add(styleEl)
      }
    }
    flattenMediaRules()

    const measure = async (): Promise<number> => {
      // body.scrollHeight (not documentElement) so position:fixed elements
      // anchored to the viewport bottom don't pad the captured area.
      let h = Math.max(doc.body.scrollHeight, doc.body.offsetHeight)
      iframe.style.height = h + 'px'
      await raf2()
      const h2 = Math.max(doc.body.scrollHeight, doc.body.offsetHeight)
      if (h2 !== h) {
        h = h2
        iframe.style.height = h + 'px'
        await raf2()
      }
      contentHeight = h
      return h
    }
    await measure()

    // Background completion of the full settle. Late-arriving assets reflow
    // the content, so remeasure; `true` tells the caller to re-render.
    const settled: Promise<boolean> = settleChain
      .then(async ({ timedOut }) => {
        if (timedOut) warnings.push({ kind: 'resource-timeout' })
        if (!pendingAtGate || disposed) return false
        await measure()
        return true
      })
      .catch(() => false)

    const session: CaptureSession = {
      doc,
      warnings,
      settled,
      width: () => width,
      contentHeight: () => contentHeight,
      async setWidth(w: number) {
        width = w
        iframe.style.width = w + 'px'
        await raf2()
        flattenMediaRules() // width changed → media evaluations may have flipped
        return measure()
      },
      remeasure: measure,
      bgInfo: () => getBgInfo(doc),
      collectTextRuns: () => collectTextRuns(doc),
      collectBreakRects: (runs) => collectBreakRects(doc, runs),
      async withBgStripped<T>(fn: () => Promise<T>): Promise<T> {
        // Save/restore the whole style ATTRIBUTE verbatim, and detect via
        // longhands. Two bugs lived here: (1) writing the `background`
        // shorthand clobbers inline longhands (a body with an inline
        // `background-image:` lost it forever on restore, so every capture
        // after the first preview render — including the export itself — saw
        // the stylesheet fallback background); (2) the computed `background`
        // shorthand is "" on Firefox, so the strip never ran there at all.
        const targets: [HTMLElement, string | null][] = []
        for (const el of [doc.documentElement, doc.body, doc.body.firstElementChild as HTMLElement | null]) {
          if (!el) continue
          if (hasVisibleBg(win.getComputedStyle(el))) {
            targets.push([el, el.getAttribute('style')])
            el.style.background = 'transparent'
          }
        }
        await new Promise<void>((r) => window.requestAnimationFrame(() => r()))
        try {
          return await fn()
        } finally {
          for (const [el, prev] of targets) {
            if (prev === null) el.removeAttribute('style')
            else el.setAttribute('style', prev)
          }
        }
      },
      async mountBgLayer(totalHeightCss: number) {
        const { css } = getBgInfo(doc)
        const el = doc.createElement('div')
        el.style.cssText = [
          'position: absolute', 'left: 0', 'top: 0',
          `width: ${width}px`, `height: ${Math.ceil(totalHeightCss)}px`,
          css, 'z-index: -9999',
        ].join(';')
        doc.body.appendChild(el)
        await raf2()
        return { el, remove: () => el.remove() }
      },
      dispose() {
        if (disposed) return
        disposed = true
        iframe.remove()
      },
    }
    return session
  } catch (e) {
    iframe.remove()
    throw e
  }
}
